<template>
  <v-card elevation="0" width="100%">
    <VehicleHistoryGraph ref="graph" v-if="graphVisible" v-on:location="showLocation" />
    <v-toolbar dense elevation="2">
      <v-tooltip style="z-index: 4000" top open-delay="500">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            small
            icon
            @click="toggleGraph()"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon v-if="graphVisible">expand_less</v-icon>
            <v-icon v-else>expand_more</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("map.tooltip.control.toggle_graph") }}</span>
      </v-tooltip>

      <v-spacer />

      <v-tooltip style="z-index: 4000" top open-delay="500">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            small
            icon
            outlined
            @click="prevWeek"
            :disabled="!canPrevWeek"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>keyboard_double_arrow_left</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("map.tooltip.control.prev_week") }}</span>
      </v-tooltip>

      <v-tooltip style="z-index: 4000" top open-delay="500">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            small
            icon
            outlined
            class="mx-2"
            @click="prevDay"
            :disabled="!canPrevDay"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>keyboard_arrow_left</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("map.tooltip.control.prev_day") }}</span>
      </v-tooltip>

      <DateChip v-for="(date, index) in dates" :key="index" :date="date" :events="getSummary"
                :selected="date===selected"
                :disabled="isDisabled(date)"
                @select="changeWeek"
                @date-click="dateClick" />

      <v-tooltip style="z-index: 4000" top open-delay="500">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            small
            icon
            outlined
            class="mx-2"
            @click="nextDay"
            :disabled="!canNextDay()"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>keyboard_arrow_right</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("map.tooltip.control.next_day") }}</span>
      </v-tooltip>

      <v-tooltip style="z-index: 4000" top open-delay="500">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            small
            icon
            outlined
            @click="nextWeek"
            :disabled="!canNextWeek()"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>keyboard_double_arrow_right</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("map.tooltip.control.next_week") }}</span>
      </v-tooltip>

      <v-spacer />

      <v-menu offset-y top z-index="4000" :close-on-content-click="false" v-model="menu">
        <template v-slot:activator="{ on: menu, attrs }">
          <v-tooltip style="z-index: 4000" top open-delay="500">
            <template v-slot:activator="{ on: tooltip }">
              <v-btn
                small
                icon
                v-bind="attrs"
                v-on="{ ...tooltip, ...menu }"
              >
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.tooltip.control.options") }}</span>
          </v-tooltip>
        </template>
        <div @mouseleave="menu = false">
          <v-list>
            <v-list-item dense link
                         v-for="(item, index) in this.$store.state.history.defs"
                         :key="index"
                         v-on:click="toggleParameter(item.name)">
              <v-list-item-content>
              <span><span
                :style="'color:' + item.color">⏺</span>&nbsp;{{ $t("map.panel.history.graph." + item.name + ".title") }}</span>
              </v-list-item-content>
              <v-list-item-icon v-if="selectedParameterIcon(item.name) !== null">
                <v-icon>{{ selectedParameterIcon(item.name) }}</v-icon>
              </v-list-item-icon>
            </v-list-item>
            <v-divider style="margin: 5px 10px 0 10px" />
            <v-list-item dense link v-on:click="clearSelectedParameters">
              {{ $t("map.panel.history.menu.cancel") }}
            </v-list-item>
            <v-list-item dense link v-on:click="exportToPDF">
              {{ $t("map.panel.history.menu.export_graph") }}
            </v-list-item>
            <v-divider style="margin:  0px 10px 0 10px" />
            <v-list-item dense link v-on:click="closeHistory">
              {{ $t("map.panel.tooltip.close") }}
            </v-list-item>
          </v-list>
        </div>
      </v-menu>

    </v-toolbar>
  </v-card>
</template>

<script>
import { DateTime } from "luxon";
import DateChip from "@/components/DateChip";
import VehicleHistoryGraph from "@/components/VehicleHistoryGraph.vue";
import Vue from "vue";

export default {
  name: "HistoryControl",
  components: { DateChip, VehicleHistoryGraph },

  data() {
    return {
      menu: false,
      dates: [],
      graphVisible: false,
      selected: null,
      summaryFetching: false,
      summaries: new Map()
    };
  },

  methods: {
    closeHistory() {
      this.$store.dispatch("history/clear");
      this.$router.routeToMap();
    },

    isDisabled(date) {
      return DateTime.fromISO(date).diff(DateTime.now().startOf("day"), "days") > 0;
    },

    canPrevDay() {
      return true;
    },

    canNextDay() {
      if (this.dates !== null && this.selected !== null)
        return DateTime.fromISO(this.selected).startOf("day") < DateTime.now().startOf("day");
      else
        return false;
    },

    canPrevWeek() {
      return true;
    },

    canNextWeek() {
      if (this.dates !== null)
        return DateTime.fromISO(this.dates[6]).diff(DateTime.now().startOf("day"), "days") <= 0;
      else
        return false;
    },

    prevDay() {
      this.changeWeek(DateTime.fromISO(this.selected).minus({ days: 1 }).toFormat("yyyy-MM-dd"));
    },

    nextDay() {
      this.changeWeek(DateTime.fromISO(this.selected).plus({ days: 1 }).toFormat("yyyy-MM-dd"));
    },

    prevWeek() {
      this.changeWeek(DateTime.fromISO(this.selected).minus({ weeks: 1 }).toFormat("yyyy-MM-dd"));
    },

    nextWeek() {
      const next_week = DateTime.fromISO(this.selected).plus({ weeks: 1 });

      if (next_week > DateTime.now().startOf("day").plus({ days: 1 }))
        this.changeWeek(DateTime.now().startOf("day"));
      else
        this.changeWeek(next_week.toFormat("yyyy-MM-dd"));
    },

    refreshHistory() {
      if (this.selected !== null)
        this.$store.dispatch("history/fetch", {
          periodFrom: DateTime.fromISO(this.selected).toFormat("yyyy-MM-dd"),
          periodTo: DateTime.fromISO(this.selected).plus({ seconds: 86399 }).toISO()
        });
      else
        this.$store.dispatch("history/fetch", {
          periodFrom: null,
          periodTo: null
        });
    },

    changeWeek(date) {
      const week = DateTime.fromISO(date).startOf("week");

      this.dates = [
        week.toFormat("yyyy-MM-dd"),
        week.plus({ days: 1 }).toFormat("yyyy-MM-dd"),
        week.plus({ days: 2 }).toFormat("yyyy-MM-dd"),
        week.plus({ days: 3 }).toFormat("yyyy-MM-dd"),
        week.plus({ days: 4 }).toFormat("yyyy-MM-dd"),
        week.plus({ days: 5 }).toFormat("yyyy-MM-dd"),
        week.plus({ days: 6 }).toFormat("yyyy-MM-dd")
      ];

      if (this.isDisabled(date))
        this.selected = null;
      else
        this.selected = date;

      this.refreshHistory();
    },

    dateClick(date) {
      this.selected = date;
      this.refreshHistory();
    },

    getSummary(date) {
      if (date === null)
        return false;

      const summary = this.summaries.get(date);

      if (summary === undefined) {

        if (this.summaryFetching)
          return false;

        // nacti dany mesic
        this.summaryFetching = true;

        this.$store.dispatch("data/fetchDaySummaries",
          {
            vehicleId: this.$store.state.history.vehicle.vehicleId,
            from: DateTime.fromISO(date).startOf("month").toFormat("yyyy-MM-dd"),
            to: DateTime.fromISO(date).endOf("month").toFormat("yyyy-MM-dd")
          }
        ).then(response => {
          response.data.forEach(x => {
            this.summaries.set(x.date, x);
          });
          this.summaryFetching = false;
        });

        return false;
      }

      var colors = [];

      if (summary.rideCount > 0) {
        if (summary.distance < 300_000)
          colors.push("blue lighten-4");
        else if (summary.distance < 700_000)
          colors.push("blue");
        else
          colors.push("blue darken-4");
      }
      if (summary.refuelingCount > 0)
        colors.push("red");

      return colors;
    },

    toggleGraph() {
      this.graphVisible = !this.graphVisible;
      Vue.$cookies.set("history-graph-visible", this.graphVisible, "14d");
      this.$emit("graph-visible", this.graphVisible);
    },

    showLocation(ts) {
      this.$emit("location", ts);
    },

    selectedParameterIcon(code) {
      switch (this.$store.state.history.selected.indexOf(code)) {
        case 0:
          return "looks_one";
        case 1:
          return "looks_two";
        case 2:
          return "looks_3";
        case 3:
          return "looks_4";
        default:
      }
    },

    toggleParameter(code) {
      this.$store.dispatch("history/toggleParameter", code);
    },

    clearSelectedParameters() {
      this.$store.dispatch("history/clearParameters");
    },

    printGraph() {
      this.menu = false;
      this.$refs.graph.print();
    },

    exportToPDF() {
      this.menu = false;
      this.$refs.graph.exportToPDF();
    }
  },

  watch: {
    date: function() {
      this.refreshHistory();
    },

    "$store.state.history.vehicleId": function() {
      this.summaryFetching = false;
      this.summaries = new Map();
    },

    "$store.state.history.periodFrom": function() {
      this.changeWeek(this.$store.state.history.periodFrom);
    }
  },

  computed: {
    vehicle() {
      return this.$store.state.history.vehicle;
    }
  },

  mounted() {
    this.graphVisible = Vue.$cookies.get("history-graph-visible") === "true";

    if (this.$store.state.history.periodFrom === null)
      this.changeWeek(DateTime.now().toFormat("yyyy-MM-dd"));
    else
      this.changeWeek(DateTime.fromISO(this.$store.state.history.periodFrom).toFormat("yyyy-MM-dd"));
  }
};
</script>

<style lang="sass" scoped>
</style>